import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import axiosInstance from '../utility/axiosInstance';
import { FaSyncAlt, FaUndo } from 'react-icons/fa';
import { useToast } from './ToastContext';
import { useConfirm } from './ConfirmContext';
import {useResourceCreationLock} from './TrialLock';

interface ManagedBackup {
    id: string;
    name: string;
    status: string;
    size_gb: number;
    created_at: string;
}

interface ManagedBackupsModalProps {
    isOpen: boolean;
    onClose: () => void;
    // API base for the managed service, e.g. '/v1/databases', '/v1/redis', '/v1/relationalDb', '/v1/qdrant', '/v1/clickhouse'
    apiBase: string;
    serviceId: number | null;
    serviceName: string;
}

const ManagedBackupsModal: React.FC<ManagedBackupsModalProps> = ({ isOpen, onClose, apiBase, serviceId, serviceName }) => {
    const creationLocked = useResourceCreationLock();
    const { showToast } = useToast();
    const { confirm } = useConfirm();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [enabled, setEnabled] = useState(false);
    const [retention, setRetention] = useState(7);
    const [backups, setBackups] = useState<ManagedBackup[]>([]);
    const [restoringId, setRestoringId] = useState<string | null>(null);
    const [backingUp, setBackingUp] = useState(false);

    const load = async () => {
        if (serviceId == null) return;
        setLoading(true);
        try {
            const { data } = await axiosInstance.get(`${apiBase}/${serviceId}/backups`);
            setEnabled(!!data.backups_enabled);
            setRetention(data.backup_retention ?? 7);
            setBackups(Array.isArray(data.backups) ? data.backups : []);
        } catch (err) {
            showToast('Failed to load backups', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isOpen && serviceId != null) load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, serviceId]);

    const saveSettings = async (nextEnabled: boolean) => {
        if (creationLocked || serviceId == null) return;
        setSaving(true);
        try {
            await axiosInstance.put(`${apiBase}/${serviceId}/backups`, { enabled: nextEnabled, retention });
            setEnabled(nextEnabled);
            showToast(nextEnabled ? 'Nightly backups enabled' : 'Nightly backups disabled', 'success');
        } catch (err: any) {
            showToast(err?.response?.data?.error?.message || 'Failed to update backups', 'error');
        } finally {
            setSaving(false);
        }
    };

    // One backup now, taken like a nightly one: it shows as 'creating' in the list and can be restored once available.
    const backUpNow = async () => {
        if (creationLocked || serviceId == null) return;
        setBackingUp(true);
        try {
            await axiosInstance.post(`${apiBase}/${serviceId}/backups`);
            showToast('Backup started.', 'success');
            await load();
        } catch (err: any) {
            showToast(err?.response?.data?.error?.message || 'Backup could not be started.', 'error');
        } finally {
            setBackingUp(false);
        }
    };

    const handleRestore = async (b: ManagedBackup) => {
        if (creationLocked || serviceId == null) return;
        const proceed = await confirm({
            title: 'Restore from backup?',
            message: `This rebuilds "${serviceName}" from the backup taken ${b.created_at ? new Date(b.created_at).toLocaleString() : 'earlier'}. The current server is replaced and the service is unavailable for a few minutes. The backup itself is not consumed.`,
            confirmText: 'Restore',
        });
        if (!proceed) return;
        setRestoringId(b.id);
        try {
            await axiosInstance.post(`${apiBase}/${serviceId}/restore`, { backup_id: b.id });
            showToast('Restore started. The service rebuilds from the backup over the next few minutes.', 'success');
            onClose();
        } catch (err: any) {
            showToast(err?.response?.data?.error?.message || 'Failed to start restore', 'error');
        } finally {
            setRestoringId(null);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={`Backups: ${serviceName}`} size="lg">
            <div style={{ padding: '20px' }}>
                {loading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '180px', flexDirection: 'column', gap: '10px' }}>
                        <FaSyncAlt className="spin" size={28} style={{ color: 'var(--accent)' }} />
                        <span style={{ color: 'var(--text-secondary)' }}>Loading backups...</span>
                    </div>
                ) : (
                    <>
                        <div className="section" style={{ marginBottom: '24px' }}>
                            <h3 style={{ borderBottom: '1px solid var(--border)', paddingBottom: '10px', marginBottom: '15px', color: 'var(--text-primary)' }}>Nightly automatic backups</h3>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--text-primary)', cursor: 'pointer' }}>
                                    <input type="checkbox" checked={enabled} disabled={saving || !!creationLocked} onChange={e => saveSettings(e.target.checked)} />
                                    {enabled ? 'On' : 'Off'}
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--text-secondary)' }}>
                                    Keep newest
                                    <input
                                        type="number"
                                        min={1}
                                        max={30}
                                        value={retention}
                                        disabled={saving || !!creationLocked}
                                        onChange={e => setRetention(Math.max(1, Math.min(30, Number(e.target.value) || 1)))}
                                        onBlur={() => { if (enabled) saveSettings(true); }}
                                        style={{ width: '64px' }}
                                    />
                                    backups
                                </label>
                            </div>
                            <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginTop: '10px' }}>
                                Backups run nightly (02:00 UTC) and are billed per GB while they exist. Turning this off stops future
                                backups but keeps the ones already taken, which keep billing until you delete them.
                            </p>
                        </div>

                        <div className="section">
                            <h3 style={{ borderBottom: '1px solid var(--border)', paddingBottom: '10px', marginBottom: '15px', color: 'var(--text-primary)' }}>
                                Available backups ({backups.length})
                            </h3>
                            {backups.length === 0 ? (
                                <p style={{ color: 'var(--text-muted)' }}>
                                    {enabled ? 'No backups yet. The first one runs at the next nightly pass.' : 'No backups yet.'}
                                </p>
                            ) : (
                                <div className="table-container">
                                    <table style={{ width: '100%', fontSize: '0.9rem' }}>
                                        <thead>
                                            <tr>
                                                <th style={{ textAlign: 'left' }}>Taken</th>
                                                <th style={{ textAlign: 'left' }}>Size</th>
                                                <th style={{ textAlign: 'left' }}>Status</th>
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {backups.map(b => (
                                                <tr key={b.id}>
                                                    <td>{b.created_at ? new Date(b.created_at).toLocaleString() : 'N/A'}</td>
                                                    <td>{b.size_gb} GB</td>
                                                    <td>{b.status}</td>
                                                    <td style={{ textAlign: 'right' }}>
                                                        <button
                                                            className="submit-button"
                                                            style={{ width: 'auto' }}
                                                            disabled={!!creationLocked || restoringId === b.id || b.status !== 'available'}
                                                            onClick={() => handleRestore(b)}
                                                        >
                                                            <FaUndo style={{ marginRight: '6px' }} />
                                                            {restoringId === b.id ? 'Restoring...' : 'Restore'}
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </>
                )}

                <div className="modal-actions" style={{ marginTop: '24px' }}>
                    <button className="cancel-button" onClick={onClose}>Close</button>
                    <button title={creationLocked ?? undefined} className="submit-button" onClick={backUpNow} disabled={!!creationLocked || loading || backingUp} style={{ width: 'auto' }}>
                        {backingUp ? 'Starting backup...' : 'Back up now'}
                    </button>
                    <button className="submit-button" onClick={load} style={{ width: 'auto' }}>
                        <FaSyncAlt style={{ marginRight: '8px' }} />
                        Refresh
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default ManagedBackupsModal;
